import clsx from 'clsx';
import { Link } from '@/lib/i18n/navigation';
import { type AnchorHTMLAttributes, type ReactNode } from 'react';
import type { ButtonColor } from './Button';
import { COLOR_CLASS_MAP } from './Button.constants';
import styles from './Button.module.scss';

export type LinkButtonVariant =
  | 'primary'
  | 'primary-outlined'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'link';
export type LinkButtonSize = 'sm' | 'md' | 'lg';

export interface LinkButtonProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  /** URL to navigate to */
  href: string;
  /** Visual style variant */
  variant?: LinkButtonVariant;
  /** Size of the button */
  size?: LinkButtonSize;
  /** Color override for CMS-driven variants */
  color?: ButtonColor;
  /** Show underline decoration */
  underline?: boolean;
  /** Apply uppercase text-transform (default: true for buttons, false for link variant) */
  uppercase?: boolean;
  /** Whether the button should take full width */
  fullWidth?: boolean;
  /** Icon to display before the label */
  iconBefore?: ReactNode;
  /** Icon to display after the label */
  iconAfter?: ReactNode;
  /** Whether to open in new tab */
  external?: boolean;
  /** Open in new tab without forcing external mode */
  openInNewTab?: boolean;
  /** Button content */
  children: ReactNode;
}

export const LinkButton = ({
  href,
  variant = 'primary',
  size = 'md',
  color,
  underline: underlineProp,
  uppercase: uppercaseProp,
  fullWidth = false,
  iconBefore,
  iconAfter,
  external = false,
  openInNewTab,
  className,
  children,
  ...props
}: LinkButtonProps) => {
  const isUppercase = uppercaseProp ?? variant !== 'link';
  const colorClass = color && COLOR_CLASS_MAP[color];
  const buttonClassName = clsx(
    styles.button,
    styles[variant],
    styles[size],
    colorClass && styles[colorClass],
    underlineProp && styles.underline,
    !isUppercase && styles.noUppercase,
    {
      [styles.fullWidth]: fullWidth,
      [styles.iconOnly]: !children && (iconBefore || iconAfter),
    },
    className
  );

  const iconContent = (
    <>
      {iconBefore && (
        <span className={styles.iconBefore} aria-hidden="true">
          {iconBefore}
        </span>
      )}
      {children && (
        <span
          className={styles.label}
          data-label={typeof children === 'string' ? children : undefined}
        >
          <span className={styles.labelText}>{children}</span>
        </span>
      )}
      {iconAfter && (
        <span className={styles.iconAfter} aria-hidden="true">
          {iconAfter}
        </span>
      )}
    </>
  );

  const shouldOpenNewTab = openInNewTab ?? external;

  if (external) {
    return (
      <a
        href={href}
        target={shouldOpenNewTab ? '_blank' : undefined}
        rel={shouldOpenNewTab ? 'noopener noreferrer' : undefined}
        className={buttonClassName}
        {...props}
      >
        {iconContent}
      </a>
    );
  }

  return (
    <Link
      href={href}
      target={shouldOpenNewTab ? '_blank' : undefined}
      rel={shouldOpenNewTab ? 'noopener noreferrer' : undefined}
      className={buttonClassName}
      {...props}
    >
      {iconContent}
    </Link>
  );
};

LinkButton.displayName = 'LinkButton';
